import { formatEther } from 'viem'
import Lock from '@/assets/icons/Lock.svg?react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import {
	useStaking2aUserStakeData,
	useStaking2aUserStakeDataWithoutLockStatus
} from '@/hooks/staking2a-contract/useStaking2aUserStakeData'
import { usePepperBalance } from '@/hooks/pepper-contract/usePepperBalance'
import { cn } from '@/lib/utils'
import { StakeDialog } from '../dialogs/StakeDialog'
import { UnstakeDialog } from '../dialogs/UnstakeDialog'
import { useResponsiveDialog } from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { useState } from 'react'

export function StakedCard({ className }: { className?: string }) {
	const format = useCurrencyFormatter(3)
	const { data: userStakedData, refetch: refetchUserStakedData } = useStaking2aUserStakeData()
	const { data: userStakedDataWithoutLockStatus, refetch: refetchUserStakedDataWithoutLockStatus } =
		useStaking2aUserStakeDataWithoutLockStatus()
	const { refetch: refetchPepperBalance } = usePepperBalance()
	const [stakeDialogProps, openStakeDialog, closeStakeDialog] = useResponsiveDialog({ resetOnClose: true })
	const [unstakeDialogProps, openUnstakeDialog, closeUnstakeDialog] = useResponsiveDialog({ resetOnClose: true })

	const [dialogKey, setDialogKey] = useState(0)

	const {
		totalStake: totalStaked = 0n,
		totalUnstakable: unlockedTokens = 0n,
		totalLocked: lockedTokens = 0n
	} = userStakedData ?? {}

	const { totalPendingUnstake: totalClaimable = 0n } = userStakedDataWithoutLockStatus ?? {}

	const refetchAll = () => {
		refetchUserStakedData()
		refetchUserStakedDataWithoutLockStatus()
		refetchPepperBalance()
	}

	const onCloseStakeDialog = () => {
		refetchAll()
		closeStakeDialog()
	}

	const onSubmitOnUnstakedDialog = () => {
		refetchAll()
	}

	const onOpenDialog = (onOpen: () => void) => {
		refetchAll()
		setDialogKey(prev => prev + 1)
		onOpen()
	}

	return (
		<Card className={cn('p-6 flex flex-col items-center justify-between gap-3', className)}>
			<div className="flex flex-col items-center">
				<img src="/images/vault.webp" alt="vault" className="size-[60px] mb-4" />
				<h4 className="text-2xl leading-none uppercase">My PEPPER Staked</h4>
				<div className="flex flex-col items-center">
					<span className="flex items-center gap-2 text-[2.75rem] md:text-[3.75rem] text-neutral-40 leading-none">
						{format(formatEther(totalStaked ?? 0n))}
					</span>
					{lockedTokens > 0 && (
						<span className="flex items-center gap-2 text-neutral-40 leading-none text-[1.75rem]">
							<Lock width={16} height={16} />
							{`${format(formatEther(lockedTokens))} locked`}
						</span>
					)}
				</div>
			</div>
			<div className="flex gap-3">
				<Button
					onClick={() => onOpenDialog(openStakeDialog)}
					variant="danger"
					className="leading-none py-5 px-10"
					data-testid={testIdFactory.pepperStakeButton()}
				>
					Stake
				</Button>
				<Button
					onClick={() => onOpenDialog(openUnstakeDialog)}
					variant="outline"
					className="leading-none py-5 px-10"
					data-testid={testIdFactory.PepperUnstakeButton()}
				>
					Unstake
				</Button>

				<StakeDialog onSubmit={onCloseStakeDialog} {...stakeDialogProps} key={dialogKey} />
				<UnstakeDialog
					onSubmit={onSubmitOnUnstakedDialog}
					closeDialog={closeUnstakeDialog}
					totalStaked={totalStaked}
					unlockedTokens={unlockedTokens}
					lockedTokens={lockedTokens}
					totalClaimable={totalClaimable}
					{...unstakeDialogProps}
					key={dialogKey}
				/>
			</div>
		</Card>
	)
}
